import { readState } from "@/__main__/app-state.mjs";
import { IS_APP, IS_DEV } from "@/__main__/constants.mjs";
import { readData } from "@/__main__/get-data.mjs";
import mainRefs from "@/__main__/refs.mjs";
import router, { EVENT_CHANGE_ROUTE } from "@/__main__/router.mjs";
import { removeSnackbarMessage, showSnackbar } from "@/app/actions.mjs";
import type { SnackbarActions, SnackbarMessageInit } from "@/app/Snackbar.jsx";
import { getCurrentPatchForStaticData } from "@/game-lol/utils/patches.mjs";
import LoLStatic from "@/game-lol/utils/static.mjs";
import { getStaticChampionByKey } from "@/game-lol/utils/static-data.mjs";
import AccountIcon from "@/inline-assets/account2.svg";
import GuidesIcon from "@/inline-assets/blitz-guides.svg";
import BlitzLogo from "@/inline-assets/blitz-logo.svg";
import GameIconLoLIcon from "@/inline-assets/game-icon-box-lol.svg";
import OverlaysIcon from "@/inline-assets/overlays.svg";
import ProBuildsIcon from "@/inline-assets/probuilds.svg";
import TfTCompsIcon from "@/inline-assets/tft-comps.svg";
import TierListIcon from "@/inline-assets/tierlist.svg";
import TrophyIcon from "@/inline-assets/trophy.svg";
import { addDismissedSnackbar } from "@/marketing/actions.mjs";
import { catchAllRoutes } from "@/routes/constants.mjs";
import { devWarn } from "@/util/dev.mjs";
import isMobile from "@/util/is-mobile.mjs";

export type MarketingSnackbarEntry =
  | [string | RegExp, SnackbarMessageInit]
  | [string, () => SnackbarMessageInit | null]
  | [RegExp, (match: RegExpExecArray) => SnackbarMessageInit | null];

export const MARKETING_SNACKBAR_ID = "marketing";

const downloadAction: SnackbarActions = {
  actions: [
    {
      text: ["common:download", "Download"],
      href: "/download",
      target: "_blank",
      onClick() {},
    },
  ],
};

const APP_SNACKBARS: ReadonlyArray<MarketingSnackbarEntry> = [];

const DOWNLOAD_SNACKBARS: ReadonlyArray<MarketingSnackbarEntry> = [
  [
    /^\/[^\\/].+?\/profile/,
    {
      id: "profiles",
      Icon: AccountIcon,
      text: [
        "common:marketing.snackbar.profiles",
        "Get your own Profile and track your stats with Blitz.",
      ],
      ...downloadAction,
    },
  ],
  [
    /^\/overlays/,
    {
      id: "overlays",
      Icon: OverlaysIcon,
      text: [
        "common:marketing.snackbar.overlays",
        "Enable these Overlays with Blitz.",
      ],
      ...downloadAction,
    },
  ],
  [
    /^\/[^\\/]+?\/leaderboards/,
    {
      id: "leaderboards",
      Icon: TrophyIcon,
      text: [
        "common:marketing.snackbar.leaderboards",
        "Climb the Leaderboard with Blitz.",
      ],
      ...downloadAction,
    },
  ],
  // League of Legends
  [
    /^\/lol\/champions\/([^\\/]+)/,
    (match) => {
      const championKey = match[1].toLowerCase();

      const patch = getCurrentPatchForStaticData();
      const champion = getStaticChampionByKey(championKey, patch);

      if (!champion) return null;

      return {
        id: "champions",
        icon: {
          type: "image",
          src: LoLStatic.getChampionImage(champion.id),
          zoom: 1.15,
          alt: champion.name,
        },
        text: [
          "common:marketing.snackbar.lol.champion",
          "Automatically import this {{champion}} build with Blitz",
          { champion: champion.name },
        ],
        ...downloadAction,
      };
    },
  ],
  [
    /^\/lol\/probuilds/,
    {
      id: "probuilds",
      Icon: ProBuildsIcon,
      text: [
        "common:marketing.snackbar.lol.probuilds",
        "Automatically import Pro Builds with Blitz.",
      ],
      ...downloadAction,
    },
  ],
  [
    /^\/lol\/tierlist/,
    {
      id: "tierlist",
      Icon: TierListIcon,
      text: [
        "common:marketing.snackbar.lol.tierlist",
        "Stay on top of the meta with Blitz.",
      ],
      ...downloadAction,
    },
  ],
  [
    /^\/lol\/match/,
    {
      id: "lol.match",
      Icon: GameIconLoLIcon,
      text: [
        "common:marketing.snackbar.lol.match",
        "Gain insights and stats only available with Blitz.",
      ],
      ...downloadAction,
    },
  ],
  // Teamfight Tactics
  [
    /^\/tft\/comps/,
    {
      id: "tft.comps",
      Icon: TfTCompsIcon,
      text: [
        "common:marketing.snackbar.tft.comps",
        "Get these Comps as an Overlay with Blitz.",
      ],
      ...downloadAction,
    },
  ],
  // Valorant
  [
    /^\/valorant\/lineups/,
    {
      id: "val.guides",
      Icon: GuidesIcon,
      text: [
        "common:marketing.snackbar.val.guides",
        "Master the pro lineups with Blitz.",
      ],
      ...downloadAction,
    },
  ],
  // Catch-all
  [
    /^(?!\/download|\/)/,
    (): SnackbarMessageInit => {
      // Allow fully dismissing welcome, but show an alternative if the user has
      if (!readState.marketing.dismissed?.includes("welcome")) {
        return {
          id: "welcome",
          icon: "👋",
          text: [
            "common:marketing.snackbar.welcome",
            "Welcome to Blitz! Download for the full experience!",
          ],
          ...downloadAction,
          dismissable: true,
          onDismissed() {
            // "Dismiss" the alternative as well, so it doesn't immediately reappear
            addDismissedSnackbar("generic");
          },
        };
      }

      return {
        id: "generic",
        Icon: BlitzLogo,
        text: [
          "common:marketing.snackbar.generic",
          "Unlock your full potential with Blitz.",
        ],
        ...downloadAction,
      };
    },
  ],
];

export const snackbars: Array<MarketingSnackbarEntry & { show?: boolean }> = [
  ...(IS_APP ? APP_SNACKBARS : []),
  ...(IS_APP || isMobile() ? [] : DOWNLOAD_SNACKBARS),
];

export function registerMarketingSnackbars() {
  // Verify that the app has routes that match the snackbar messages
  if (IS_DEV) {
    for (const [path] of snackbars) {
      const match = mainRefs.routes
        .filter((route) => !route[catchAllRoutes])
        .find((route) => {
          if (path instanceof RegExp) {
            if (route.path instanceof RegExp) {
              // Not a perfect check, but it works. Checks if
              // the snackbar path can match against the route's regex
              // source (omitting the leading ^, and removing \ and +)

              let match = route.path.source
                .replace(/\\/g, "")
                .replace(/\+/g, "");

              if (match.startsWith("^")) {
                match = match.substring(1);
              }

              return path.test(match);
            }

            return path.test(route.path);
          }

          if (route.path instanceof RegExp) {
            return route.path.test(path);
          }

          return route.path === path;
        });

      if (!match) {
        devWarn("No route found for snackbar message:", path);
      }
    }
  }

  updateMarketingSnackbar();

  router.events.on(EVENT_CHANGE_ROUTE, updateMarketingSnackbar);
}

async function updateMarketingSnackbar() {
  if (
    !router.route ||
    router.previousRoute?.currentPath === router.route?.currentPath
  ) {
    return;
  }

  const permanentlyDismissed = await readData(["marketing", "dismissed"], {
    initializeOnce: true,
  });

  const transientlyDismissed = readState.volatile.marketing?.dismissed;

  let longestLength = -1;
  let bestMatch: SnackbarMessageInit | null = null;

  for (const [path, message] of snackbars) {
    if (path instanceof RegExp) {
      const match = path.exec(router.route.currentPath);

      if (match) {
        const len = match[0].length;

        if (len > longestLength) {
          const resolvedMessage =
            typeof message === "function" ? message(match) : message;
          if (!resolvedMessage) continue;
          longestLength = len;
          bestMatch = resolvedMessage;
        }
      }
    } else if (
      typeof path === "string" &&
      path === router.route.currentPath.toLowerCase()
    ) {
      const resolvedMessage =
        typeof message === "function"
          ? (message as () => SnackbarMessageInit)()
          : message;

      if (!resolvedMessage) continue;
      longestLength = path.length;
      bestMatch = resolvedMessage;

      break;
    }
  }

  if (bestMatch !== null) {
    const message: SnackbarMessageInit = {
      priority: "low",
      ...bestMatch,
      id: MARKETING_SNACKBAR_ID,
    };

    if (bestMatch.id) {
      const isDismissed =
        permanentlyDismissed?.includes(bestMatch.id) ||
        Date.now() < (transientlyDismissed?.[bestMatch.id] ?? 0);

      if (!isDismissed) {
        const innerDismissed = message.onDismissed;

        message.onDismissed = () => {
          addDismissedSnackbar(bestMatch.id, {
            permanent: bestMatch.dismissable === true,
          });

          innerDismissed?.();
        };
      } else {
        removeSnackbarMessage(MARKETING_SNACKBAR_ID);
        return;
      }
    }

    showSnackbar(message);
  } else {
    removeSnackbarMessage(MARKETING_SNACKBAR_ID);
  }
}
